import { type Shortcut, shortcutFor } from '../domain/shortcuts';

/**
 * Tipkovnične bližnjice (P2 §8.8): `Space` · `←` `→` · `↑` `↓` · `M`.
 *
 * Ta datoteka je samo **prevod dogodka v namero**; katera bližnjica pripada
 * kateri tipki in kdaj ne velja nobena, odloča `domain/shortcuts.ts` — tam so
 * tudi vsa pravila in njihovi razlogi.
 *
 * Seznam bližnjic je viden v Nastavitvah: bližnjica, ki je nikjer ne piše, je
 * funkcija samo za tistega, ki jo že pozna.
 */

export interface KeyboardDeps {
  onToggle(): void;
  onPrevious(): void;
  onNext(): void;
  /** Sprememba glasnosti v **korakih drsnika**, +1 ali −1. */
  onVolumeStep(direction: 1 | -1): void;
  onMuteToggle(): void;
}

/** Elementi, ki tipkovnico potrebujejo zase. */
const TYPING = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/** Elementi, ki jim presledek pripada kot sprožilec. */
const ACTIVATABLE = 'button, a[href], summary, [role="menuitem"], [role="switch"], [role="tab"]';

/** Seznam zavihkov: `←` in `→` sta tam predpisan način premikanja (APG). */
const TABLIST = '[role="tablist"]';

function matches(target: EventTarget | null, selector: string): boolean {
  return target instanceof Element && target.closest(selector) !== null;
}

export function startKeyboard(deps: KeyboardDeps, target: EventTarget = window): () => void {
  const run: Record<Shortcut, () => void> = {
    toggle: deps.onToggle,
    previous: deps.onPrevious,
    next: deps.onNext,
    volumeUp: () => {
      deps.onVolumeStep(1);
    },
    volumeDown: () => {
      deps.onVolumeStep(-1);
    },
    mute: deps.onMuteToggle,
  };

  function onKeyDown(event: Event): void {
    if (!(event instanceof KeyboardEvent)) return;

    const active = document.activeElement;
    const shortcut = shortcutFor({
      key: event.key,
      modified: event.ctrlKey || event.metaKey || event.altKey,
      composing: event.isComposing,
      typing: matches(event.target, TYPING),
      activatable: matches(event.target, ACTIVATABLE),
      inTablist: matches(event.target, TABLIST),
      focused: active !== null && active !== document.body,
    });
    if (shortcut === null) return;

    // Brez tega presledek stran prelista navzdol, puščici pa jo zadrsata — in
    // to hkrati z dejanjem, ki ga je uporabnik zahteval.
    event.preventDefault();
    run[shortcut]();
  }

  target.addEventListener('keydown', onKeyDown);
  return () => {
    target.removeEventListener('keydown', onKeyDown);
  };
}
